import { computed, readonly, ref, shallowRef } from "vue";
import {
  addWishlist,
  addWishlistBulkItem,
  addWishlistItems,
  changeWishlist,
  deleteWishlist,
  deleteWishlistItem,
  getWishList,
  getWishlists,
  getWishlistItems,
  updateWishlistItems,
} from "@/core/api/graphql/account";
import { SortDirection } from "@/core/enums";
import { Logger, asyncForEach } from "@/core/utilities";
import { useUser } from "@/shared/account";
import type {
  InputAddWishlistBulkItemType,
  InputRemoveWishlistItemType,
  InputUpdateWishlistItemsType,
  InputAddWishlistItemsType,
  WishlistType,
  LineItemType,
} from "@/core/api/graphql/types";
import type { ChangeWishlistPayloadType, CreateWishlistPayloadType } from "@/core/types";
import type { Ref } from "vue";

export const DEFAULT_WISHLISTS_PER_PAGE = 50;

const loading = ref(true);
const loadingMore = ref(true);
const lists = shallowRef<WishlistType[]>([]);
const list: Ref<WishlistType | undefined> = ref();
const itemsPerPage = ref(DEFAULT_WISHLISTS_PER_PAGE);
const pages: Ref<number> = ref(0);
const listItemPages: Ref<number> = ref(0);
const page: Ref<number> = ref(1);
const listItems: Ref<LineItemType[] | undefined> = ref();

export function useWishlists(options: { autoRefetch: boolean } = { autoRefetch: true }) {
  const { fetchUser, user } = useUser();

  async function createWishlist(payload: CreateWishlistPayloadType): Promise<string | undefined> {
    let newList: WishlistType;
    loading.value = true;

    try {
      newList = await addWishlist(payload);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${createWishlist.name}`, e);
      throw e;
    }

    if (options.autoRefetch) {
      await fetchWishlists();
    }
    return newList.id;
  }

  async function updateWishlist(payload: ChangeWishlistPayloadType): Promise<void> {
    loading.value = true;

    try {
      list.value = await changeWishlist(payload);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${updateWishlist.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }

    if (options.autoRefetch) {
      await fetchWishlists();
    }
  }

  async function fetchWishlists(): Promise<void> {
    loading.value = true;

    // caching fix impersonate Innovadis
    await fetchUser({ withBroadcast: true });

    try {
      const response = await getWishlists({
        sort: `name:${SortDirection.Descending}`,
        first: itemsPerPage.value,
        after: String((page.value - 1) * itemsPerPage.value),
        customerId: user.value.id,
      });

      lists.value = response.items ?? [];
      pages.value = Math.ceil((response.totalCount ?? 0) / itemsPerPage.value);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${fetchWishlists.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchWishList(listId: string) {
    loading.value = true;

    try {
      list.value = await getWishList(listId);

      await fetchWishListItems(listId);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${fetchWishList.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchWishListItems(listId: string) {
    loading.value = true;

    const ITEMS_PER_PAGE = 9999;

    try {
      const { items = [], totalCount = 0 } = await getWishlistItems({
        first: ITEMS_PER_PAGE,
        after: String((page.value - 1) * ITEMS_PER_PAGE),
        cartId: listId,
      });

      listItems.value = items;

      listItemPages.value = Math.ceil((totalCount ?? 0) / ITEMS_PER_PAGE);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${fetchWishlists.name}`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMoreWishListItems(listId: string, extraPage: number) {
    loadingMore.value = true;

    const ITEMS_PER_PAGE = 15;

    try {
      const result = await getWishlistItems({
        first: ITEMS_PER_PAGE,
        after: String((extraPage - 1) * ITEMS_PER_PAGE),
        cartId: listId,
      });

      listItems.value = [...(listItems.value || []), ...result.items!];

      listItemPages.value = Math.ceil((result.totalCount ?? 0) / ITEMS_PER_PAGE);
    } catch (e) {
      Logger.error(`${fetchMoreWishListItems.name}`, e);
      throw e;
    } finally {
      loadingMore.value = false;
    }
  }

  async function removeWishlist(listId: string): Promise<boolean> {
    let result = false;

    loading.value = true;

    try {
      result = await deleteWishlist(listId);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${removeWishlist.name}`, e);
      throw e;
    }

    if (options.autoRefetch) {
      await fetchWishlists();
    }

    return result;
  }

  async function addItemsToWishlists(payloads: InputAddWishlistBulkItemType) {
    loading.value = true;

    try {
      await addWishlistBulkItem(payloads);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${addItemsToWishlists.name}`, e);
      throw e;
    }

    loading.value = false;
  }

  async function removeItemsFromWishlists(payload: InputRemoveWishlistItemType[]) {
    // Custom Innovadis. Remove loading to refetch wishlist to keep add to cart qty of items
    // loading.value = true

    await asyncForEach(payload, async (item) => {
      try {
        await deleteWishlistItem(item);
      } catch (e) {
        Logger.error(`${useWishlists.name}.${removeItemsFromWishlists.name}`, e);
        throw e;
      }
    });
  }

  async function updateItemsInWishlist(payload: InputUpdateWishlistItemsType): Promise<void> {
    loading.value = true;

    try {
      await updateWishlistItems(payload);
      await fetchWishList(payload.listId);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${updateItemsInWishlist.name}`, e);
    } finally {
      loading.value = false;
    }
  }

  /** @deprecated */
  function clearList() {
    list.value = undefined;
  }

  // Innovadis custom query
  async function addMultipleItemsToWishlist(payloads: InputAddWishlistItemsType) {
    loading.value = true;
    try {
      await addWishlistItems(payloads);
    } catch (e) {
      Logger.error(`${useWishlists.name}.${addMultipleItemsToWishlist.name}`, e);
      throw e;
    }
    loading.value = false;
  }

  return {
    fetchWishlists,
    fetchWishList,
    fetchWishListItems,
    fetchMoreWishListItems,
    addMultipleItemsToWishlist,
    createWishlist,
    removeWishlist,
    addItemsToWishlists,
    updateWishlist,
    updateItemsInWishlist,
    removeItemsFromWishlists,
    clearList,
    itemsPerPage,
    pages,
    listItemPages,
    page,
    loading: readonly(loading),
    loadingMore: readonly(loadingMore),
    lists: computed(() => lists.value),
    list: computed(() => list.value),
    listItems: computed(() => listItems.value),
  };
}
